/**
 * SupplierPaymentDialog
 * ---------------------
 * Records one payment that can settle several supplier invoices at once — the
 * way a commercial wire actually works. Each covered invoice gets its own
 * amount box next to the invoice number.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Banknote, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const METHODS = [
  { id: "wire", label: "Commercial wire" },
  { id: "ach", label: "ACH transfer" },
  { id: "check", label: "Check" },
  { id: "card", label: "Credit card" },
  { id: "qbo_bill_pay", label: "QuickBooks bill pay" },
  { id: "other", label: "Other" },
];

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const SupplierPaymentDialog = ({ open, onClose, token, linkType, linkId, invoices, onSaved }) => {
  const [method, setMethod] = useState("wire");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [bankName, setBankName] = useState("Frost Bank");
  const [accountName, setAccountName] = useState("Sterile Mate LLC");
  const [last4, setLast4] = useState("");
  const [reference, setReference] = useState("");
  const [contactName, setContactName] = useState("");
  const [notes, setNotes] = useState("");
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const openInvoices = useMemo(
    () => (invoices || []).filter((i) => i.status !== "void" && (i.balance ?? i.amount) > 0.009),
    [invoices],
  );

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setReference("");
    setNotes("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
  }, [open]);

  if (!open) return null;

  const toggle = (inv) =>
    setPicked((p) => {
      const next = { ...p };
      if (next[inv.id] !== undefined) delete next[inv.id];
      else next[inv.id] = String(inv.balance ?? inv.amount ?? "");
      return next;
    });

  const total = Object.values(picked).reduce((s, v) => s + (Number(v) || 0), 0);
  const supplierOf = openInvoices.find((i) => picked[i.id] !== undefined)?.supplier_name;

  const save = async () => {
    const allocations = Object.entries(picked)
      .filter(([, v]) => Number(v) > 0)
      .map(([invoice_id, v]) => ({ invoice_id, amount: Number(v) }));
    if (!allocations.length) { toast.error("Tick at least one invoice and enter the amount paid"); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/payables/payments`, {
        link_type: linkType, link_id: linkId, allocations, method,
        payment_date: paymentDate, reference, bank_name: bankName,
        account_name: accountName, account_last4: last4,
        contact_name: contactName, notes,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Payment of ${money(total)} recorded`);
      onSaved?.(r.data.payment);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record that payment");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="supplier-payment-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 inline-flex items-center gap-2">
              <Banknote className="w-5 h-5 text-emerald-600" /> Record a supplier payment
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              One wire can settle several invoices — key the amount next to each one.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Payment method">
              <select value={method} onChange={(e) => setMethod(e.target.value)}
                className="w-full h-9 text-sm border border-slate-300 rounded-md px-2"
                data-testid="payment-method">
                {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </Field>
            <Field label="Date transmitted">
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                className="h-9 text-sm" data-testid="payment-date" />
            </Field>
            <Field label="Reference / trace #">
              <Input value={reference} onChange={(e) => setReference(e.target.value)}
                className="h-9 text-sm" placeholder="Fed reference" data-testid="payment-reference" />
            </Field>
            <Field label="Originating institution">
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} className="h-9 text-sm" />
            </Field>
            <Field label="Originating account name">
              <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} className="h-9 text-sm" />
            </Field>
            <Field label="Account ending in">
              <Input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="h-9 text-sm" placeholder="2844" data-testid="payment-last4" />
            </Field>
          </div>

          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">
              Covered invoices {supplierOf ? `· ${supplierOf}` : ""}
            </p>
            {openInvoices.length === 0 ? (
              <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-4">
                No open supplier invoices on this record yet — add the invoice first.
              </p>
            ) : (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {openInvoices.map((inv) => {
                  const on = picked[inv.id] !== undefined;
                  return (
                    <div key={inv.id} className="flex items-center gap-3 px-3 py-2 flex-wrap">
                      <input type="checkbox" checked={on} onChange={() => toggle(inv)}
                        className="w-4 h-4 accent-emerald-600" data-testid={`pay-pick-${inv.invoice_number}`} />
                      <div className="flex-1 min-w-[160px]">
                        <p className="text-sm font-bold text-slate-900">Invoice #{inv.invoice_number}</p>
                        <p className="text-[11px] text-slate-500">
                          {inv.supplier_name} · billed {money(inv.amount)} · open {money(inv.balance ?? inv.amount)}
                          {inv.po_number ? ` · PO ${inv.po_number}` : ""}
                        </p>
                      </div>
                      <div className="w-32">
                        <Input type="number" step="0.01" disabled={!on}
                          value={on ? picked[inv.id] : ""}
                          onChange={(e) => setPicked((p) => ({ ...p, [inv.id]: e.target.value }))}
                          className="h-9 text-sm text-right font-semibold"
                          placeholder="0.00" data-testid={`pay-amount-${inv.invoice_number}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Their AP contact first name (for the email greeting)">
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)}
                className="h-9 text-sm" placeholder="Tony" />
            </Field>
            <Field label="Internal notes">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-sm" />
            </Field>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
          <p className="text-sm text-slate-600">
            Total payment <span className="font-black text-slate-900" data-testid="payment-total">{money(total)}</span>
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving || total <= 0}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="payment-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Record payment
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">{label}</span>
    {children}
  </label>
);

export default SupplierPaymentDialog;
